import { useState, useEffect } from 'react';
import { X, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

// Sale window: April 11, 2026 12:00 AM MST to April 13, 2026 12:00 AM MST
// MST = UTC-7
const SALE_START = new Date('2026-04-11T07:00:00Z'); // April 11 00:00 MST
const SALE_END = new Date('2026-04-13T07:00:00Z');   // April 13 00:00 MST
const PROMO_CODE = 'CWF20';
const DISCOUNT_PERCENT = 20;

export const isSaleActive = () => {
  const now = new Date();
  return now >= SALE_START && now < SALE_END;
};

export const SALE_CONFIG = {
  code: PROMO_CODE,
  discount: DISCOUNT_PERCENT,
  start: SALE_START,
  end: SALE_END,
  isActive: isSaleActive,
};

export const SalePopup = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSaleActive()) return;
    const dismissed = sessionStorage.getItem('cwf-sale-dismissed');
    if (!dismissed) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem('cwf-sale-dismissed', 'true');
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="sale-popup-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Popup */}
      <div
        className="relative w-full max-w-md bg-[#0A0A0A] border-2 border-[#C41E3A] rounded-lg overflow-hidden animate-fade-in-up shadow-2xl"
        data-testid="sale-popup"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 text-gray-400 hover:text-white transition-colors"
          data-testid="sale-popup-close"
        >
          <X size={22} />
        </button>

        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#C41E3A] via-white to-[#C41E3A]" />

        {/* Content */}
        <div className="px-6 pt-8 pb-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#C41E3A]/15 border border-[#C41E3A]/40 rounded-full px-4 py-1.5 mb-5">
            <Tag size={14} className="text-[#C41E3A]" />
            <span className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest">Limited Time</span>
          </div>

          {/* Discount */}
          <h2 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-1 tracking-tight">
            {DISCOUNT_PERCENT}% <span className="text-[#C41E3A]">OFF</span>
          </h2>
          <p className="text-gray-300 text-base mb-5">
            All Peptides — <span className="text-white font-semibold">48 Hours Only</span>
          </p>

          {/* Promo code box */}
          <div className="bg-[#121212] border border-[#27272A] rounded-md py-3 px-4 mb-5">
            <p className="text-gray-500 text-[11px] uppercase tracking-widest mb-1">Use Code at Checkout</p>
            <p className="text-white text-2xl font-bold font-mono tracking-[0.25em]" data-testid="sale-popup-code">
              {PROMO_CODE}
            </p>
          </div>

          {/* Timer hint */}
          <p className="text-gray-500 text-xs mb-6">
            Ends April 13, 2026 at midnight MST
          </p>

          {/* CTA */}
          <Link
            to="/#products"
            onClick={handleClose}
            className="inline-block w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3.5 rounded-md transition-all duration-300"
            data-testid="sale-popup-shop-btn"
          >
            Shop the Sale
          </Link>
        </div>

        {/* Bottom accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#C41E3A] via-white to-[#C41E3A]" />
      </div>
    </div>
  );
};
